import { transcriptionFrenchApi, type TranslationSourceDatasetResponse } from '@/api/transcriptionFrench.api';
import { isOfflineDbAvailable, isReserveFresh, phraseReserve } from '@/features/offline';
import { randomId } from '@/features/offline/submission-headers';
import { normalizeDatasetEntries } from './helpers';
import type { PhraseEntry } from './types';

/**
 * Phrases of "Traduire" kept on the device for offline work: one shuffled
 * list per account and language, fetched in pages of 100 with its own seed,
 * up to about 300 phrases. Offline, batches are taken from the phrases not yet
 * translated nor skipped.
 */

const RESERVE_CHUNK = 0;
const FETCH_LIMIT = 100;
const FETCH_PAGES = 3;
/** Below this many open phrases, the reserve is refilled. */
const LOW_WATER = 100;
const MAX_KEPT = 400;
const MIN_REFILL_INTERVAL_MS = 10 * 60_000;

export type DatasetPageFetcher = (page: number, cursor: number, seed: string) => Promise<TranslationSourceDatasetResponse>;

export async function readTranslateReserve(ownerId: string, lang: string): Promise<PhraseEntry[]> {
  if (!isOfflineDbAvailable()) return [];
  const record = await phraseReserve.get<PhraseEntry>(ownerId, 'translate', lang, RESERVE_CHUNK);
  return record?.items ?? [];
}

/** The next batch of open phrases, and whether more are left after it. */
export function offlineTranslateBatch(
  reserve: PhraseEntry[],
  done: Set<string>,
  pageSize: number,
): { phrases: PhraseEntry[]; hasMore: boolean } {
  const open = reserve.filter((phrase) => !done.has(phrase.id));
  return { phrases: open.slice(0, pageSize), hasMore: open.length > pageSize };
}

const toppingUp = new Set<string>();

/**
 * Refills the reserve when it is old or running low. Runs in the background
 * while online; a failure leaves the current reserve as it is.
 */
export async function topUpTranslateReserve(
  ownerId: string,
  lang: string,
  done: Set<string>,
  fetchPage: DatasetPageFetcher = (page, cursor, seed) =>
    transcriptionFrenchApi.getTranslationSourceDataset(lang, {
      page,
      limit: FETCH_LIMIT,
      excludeUserId: ownerId,
      shuffleSeed: seed,
      cursor,
    }),
): Promise<void> {
  if (!isOfflineDbAvailable() || !ownerId || !lang) return;
  const key = `${ownerId}|${lang}`;
  if (toppingUp.has(key)) return;
  toppingUp.add(key);
  try {
    const existing = await phraseReserve.get<PhraseEntry>(ownerId, 'translate', lang, RESERVE_CHUNK);
    const open = (existing?.items ?? []).filter((phrase) => !done.has(phrase.id));
    if (isReserveFresh(existing) && open.length >= LOW_WATER) return;
    // A small dataset never reaches LOW_WATER: do not refetch it on every page.
    if (existing && Date.now() - existing.savedAt < MIN_REFILL_INTERVAL_MS) return;

    const seed = randomId();
    let cursor = 0;
    const fetched: PhraseEntry[] = [];
    for (let page = 1; page <= FETCH_PAGES; page += 1) {
      const response = await fetchPage(page, cursor, seed);
      fetched.push(...normalizeDatasetEntries(response, page));
      if (typeof response?.nextCursor !== 'number') break;
      cursor = response.nextCursor;
    }

    // The server already leaves out what this account translated; the local
    // list also knows what was translated offline and not sent yet.
    const seen = new Set<string>();
    const merged = [...fetched, ...open].filter((phrase) => {
      if (done.has(phrase.id) || seen.has(phrase.id)) return false;
      seen.add(phrase.id);
      return true;
    });
    if (merged.length > 0) {
      await phraseReserve.save(ownerId, 'translate', lang, RESERVE_CHUNK, merged.slice(0, MAX_KEPT));
    }
  } finally {
    toppingUp.delete(key);
  }
}
